import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  type SortingStrategy,
} from '@dnd-kit/sortable';
import {useCallback, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

import styles from './Dashboard.module.css';
import {logDashboardInteraction} from './dashboardAnalytics';
import {TileId} from './dashboardModel';
import {type CellPosition, type GridSpan, packDenseGrid} from './denseGridPacking';

interface OverlaySize {
  width: number | null;
  height: number | null;
}

interface GridGeometry {
  cols: number;
  /** Distance between the start of one column and the next (track width + gap). */
  colPitch: number;
  /** Distance between the start of one row and the next (row height + gap). */
  rowPitch: number;
}

interface DashboardEditGridProps {
  tileIds: TileId[];
  onSetOrder: (order: TileId[]) => void;
  getTileSpan: (tileId: TileId) => GridSpan;
  renderOverlay: (tileId: TileId) => React.ReactNode;
  renderTile: (tileId: TileId) => React.ReactNode;
}

const findSortableNode = (tileId: TileId): HTMLElement | null => {
  if (typeof document === 'undefined') {
    return null;
  }
  return document.querySelector<HTMLElement>(`[data-dashboard-sortable-id="${tileId}"]`);
};

const measureSortableNode = (tileId: TileId): OverlaySize => {
  const node = findSortableNode(tileId);
  if (!node) {
    return {width: null, height: null};
  }
  const rect = node.getBoundingClientRect();
  return {
    width: rect.width > 0 ? rect.width : null,
    height: rect.height > 0 ? rect.height : null,
  };
};

const captureGeometry = (tileId: TileId): GridGeometry | null => {
  const grid = findSortableNode(tileId)?.parentElement;
  if (!grid) {
    return null;
  }
  const cs = getComputedStyle(grid);
  const colTracks = cs.gridTemplateColumns.split(' ').filter(Boolean);
  const cols = colTracks.length || 1;
  const colWidth = parseFloat(colTracks[0]) || 0;
  const colGap = parseFloat(cs.columnGap) || 0;
  const rowGap = parseFloat(cs.rowGap) || 0;
  const rowHeight = parseFloat(cs.gridAutoRows) || 0;
  return {
    cols,
    colPitch: colWidth + colGap,
    rowPitch: rowHeight + rowGap,
  };
};

export const DashboardEditGrid = ({
  tileIds,
  onSetOrder,
  getTileSpan,
  renderOverlay,
  renderTile,
}: DashboardEditGridProps) => {
  const [activeId, setActiveId] = useState<TileId | null>(null);
  const [overlaySize, setOverlaySize] = useState<OverlaySize>({width: null, height: null});
  const cursorStyleRef = useRef<HTMLStyleElement | null>(null);
  const geometryRef = useRef<GridGeometry | null>(null);
  const tileIdsRef = useRef(tileIds);
  tileIdsRef.current = tileIds;
  const packCacheRef = useRef<{
    key: string;
    current: Map<TileId, CellPosition>;
    candidate: Map<TileId, CellPosition>;
  } | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {distance: 5},
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Drive the live preview from a dense-grid simulation so tiles slide to the
  // exact cells a reorder will produce — respecting wide/tall/large spans —
  // instead of dnd-kit's uniform-cell assumption (which mis-shifts big tiles).
  const sortingStrategy = useCallback<SortingStrategy>(
    ({activeIndex, overIndex, index}) => {
      const geo = geometryRef.current;
      if (!geo || activeIndex < 0 || overIndex < 0) {
        return null;
      }

      const order = tileIdsRef.current;
      const key = `${activeIndex}:${overIndex}`;
      if (!packCacheRef.current || packCacheRef.current.key !== key) {
        const candidate = arrayMove(order, activeIndex, overIndex);
        packCacheRef.current = {
          key,
          current: packDenseGrid(order, getTileSpan, geo.cols),
          candidate: packDenseGrid(candidate, getTileSpan, geo.cols),
        };
      }

      const id = order[index];
      const from = packCacheRef.current.current.get(id);
      const to = packCacheRef.current.candidate.get(id);
      if (!from || !to) {
        return null;
      }

      return {
        x: (to.col - from.col) * geo.colPitch,
        y: (to.row - from.row) * geo.rowPitch,
        scaleX: 1,
        scaleY: 1,
      };
    },
    [getTileSpan]
  );

  const cleanupDrag = () => {
    setActiveId(null);
    setOverlaySize({width: null, height: null});
    geometryRef.current = null;
    packCacheRef.current = null;
    cursorStyleRef.current?.remove();
    cursorStyleRef.current = null;
  };

  const handleDragStart = (event: DragStartEvent) => {
    const id = event.active.id as TileId;
    setActiveId(id);
    geometryRef.current = captureGeometry(id);
    packCacheRef.current = null;

    const initialRect = event.active.rect.current.initial;
    const measured = measureSortableNode(id);
    setOverlaySize({
      width: measured.width ?? initialRect?.width ?? null,
      height: measured.height ?? initialRect?.height ?? null,
    });

    const style = document.createElement('style');
    style.textContent = '* { cursor: grabbing !important; }';
    document.head.appendChild(style);
    cursorStyleRef.current = style;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;
    cleanupDrag();

    if (over == null || active.id === over.id) {
      return;
    }

    const order = tileIdsRef.current;
    const oldIndex = order.indexOf(active.id as TileId);
    const newIndex = order.indexOf(over.id as TileId);
    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
      return;
    }
    logDashboardInteraction({
      action: 'tile_reorder',
      tileId: active.id as TileId,
      value: String(newIndex),
    });
    onSetOrder(arrayMove(order, oldIndex, newIndex));
  };

  const overlay = (
    <DragOverlay
      adjustScale={false}
      dropAnimation={null}
      style={{overflow: 'visible'}}
      zIndex={9999}
    >
      {activeId != null ? (
        <div
          className={styles.tileDragOverlay}
          style={{
            ...(overlaySize.width != null
              ? {width: overlaySize.width, minWidth: overlaySize.width, maxWidth: overlaySize.width}
              : {}),
            ...(overlaySize.height != null
              ? {
                  height: overlaySize.height,
                  minHeight: overlaySize.height,
                  maxHeight: overlaySize.height,
                }
              : {}),
          }}
        >
          {renderOverlay(activeId)}
        </div>
      ) : null}
    </DragOverlay>
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={cleanupDrag}
    >
      <SortableContext items={tileIds} strategy={sortingStrategy}>
        {tileIds.map((tileId) => renderTile(tileId))}
      </SortableContext>
      {typeof document !== 'undefined' ? createPortal(overlay, document.body) : overlay}
    </DndContext>
  );
};
